import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import axiosInstance from "api/axiosInstance";
import { API_ENDPOINTS } from "api/endpoints";
import type {
  MasterProduct,
  MasterProductPayload,
  MasterProductsState,
} from "cmspages/MasterProducts/types";

type ApiResponse<T> = {
  success?: boolean;
  message?: string;
  data?: T;
};

const getApiErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;

    if (!axiosError.response) {
      return "Network error. Please try again.";
    }

    return (
      axiosError.response.data?.message ||
      "Something went wrong. Please try again."
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
};

const extractList = (responseData: unknown): MasterProduct[] => {
  if (Array.isArray(responseData)) return responseData as MasterProduct[];

  if (!responseData || typeof responseData !== "object") return [];

  const data = responseData as {
    data?: unknown;
    masterProducts?: unknown;
    results?: unknown;
  };

  if (Array.isArray(data.data)) return data.data as MasterProduct[];
  if (Array.isArray(data.masterProducts)) {
    return data.masterProducts as MasterProduct[];
  }
  if (Array.isArray(data.results)) return data.results as MasterProduct[];

  return [];
};

const extractItem = (responseData: unknown): MasterProduct | null => {
  if (!responseData || typeof responseData !== "object") return null;

  const data = responseData as { data?: unknown };
  const candidate = data.data || responseData;

  if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
    return candidate as MasterProduct;
  }

  return null;
};

export const fetchMasterProducts = createAsyncThunk<
  MasterProduct[],
  void,
  { rejectValue: string }
>("masterProducts/fetchMasterProducts", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<ApiResponse<MasterProduct[]>>(
      API_ENDPOINTS.masterProducts.list
    );

    if (response.data.success === false) {
      return rejectWithValue(
        response.data.message || "Unable to fetch master products."
      );
    }

    return extractList(response.data);
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const createMasterProduct = createAsyncThunk<
  MasterProduct,
  MasterProductPayload,
  { rejectValue: string }
>("masterProducts/createMasterProduct", async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<ApiResponse<MasterProduct>>(
      API_ENDPOINTS.masterProducts.create,
      payload
    );

    if (response.data.success === false) {
      return rejectWithValue(
        response.data.message || "Unable to create master product."
      );
    }

    const product = extractItem(response.data);

    if (!product) {
      return rejectWithValue("Unable to create master product.");
    }

    return product;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const updateMasterProduct = createAsyncThunk<
  MasterProduct,
  { id: number; payload: MasterProductPayload },
  { rejectValue: string }
>(
  "masterProducts/updateMasterProduct",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put<ApiResponse<MasterProduct>>(
        API_ENDPOINTS.masterProducts.update(id),
        payload
      );

      if (response.data.success === false) {
        return rejectWithValue(
          response.data.message || "Unable to update master product."
        );
      }

      const product = extractItem(response.data);

      if (!product) {
        return rejectWithValue("Unable to update master product.");
      }

      return product;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  }
);

export const deleteMasterProduct = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("masterProducts/deleteMasterProduct", async (id, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete<ApiResponse<unknown>>(
      API_ENDPOINTS.masterProducts.delete(id)
    );

    if (response.data?.success === false) {
      return rejectWithValue(
        response.data.message || "Unable to delete master product."
      );
    }

    return id;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

const initialState: MasterProductsState = {
  products: [],
  loading: false,
  actionLoading: false,
  error: null,
};

const masterProductsSlice = createSlice({
  name: "masterProducts",
  initialState,
  reducers: {
    clearMasterProductsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMasterProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchMasterProducts.fulfilled,
        (state, action: PayloadAction<MasterProduct[]>) => {
          state.products = action.payload;
          state.loading = false;
        }
      )
      .addCase(fetchMasterProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Unable to fetch master products.";
      })
      .addCase(createMasterProduct.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(createMasterProduct.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.products = [action.payload, ...state.products];
      })
      .addCase(createMasterProduct.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Unable to create master product.";
      })
      .addCase(updateMasterProduct.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(updateMasterProduct.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.products = state.products.map((product) =>
          product.id === action.payload.id ? action.payload : product
        );
      })
      .addCase(updateMasterProduct.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Unable to update master product.";
      })
      .addCase(deleteMasterProduct.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(deleteMasterProduct.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.products = state.products.filter(
          (product) => product.id !== action.payload
        );
      })
      .addCase(deleteMasterProduct.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Unable to delete master product.";
      });
  },
});

export const { clearMasterProductsError } = masterProductsSlice.actions;
export default masterProductsSlice.reducer;
